import { LanguageSelector } from '@gagovictor/shared-frontend-core/dist/i18n';
import { EdgePullToRefresh } from '@gagovictor/shared-frontend-core/dist/shared/components/EdgePullToRefresh';
import { Emoji } from '@gagovictor/shared-frontend-core/dist/shared/components/Emoji';
import { GetOnMobileButton } from '@gagovictor/shared-frontend-core/dist/shared/components/GetOnMobile';
import { PwaInstallPrompt } from '@gagovictor/shared-frontend-core/dist/shared/components/PwaInstallPrompt';
import { ThemePicker } from '@gagovictor/shared-frontend-core/dist/shared/components/ThemePicker';
import type { ThemeMode } from '@gagovictor/shared-frontend-core/dist/shared/redux/preferencesSlice';
import { setTheme } from '@gagovictor/shared-frontend-core/dist/shared/redux/preferencesSlice';
import { useResolvedThemeMode } from '@gagovictor/shared-frontend-core/dist/shared/theme';
import BrushRoundedIcon from '@mui/icons-material/BrushRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import {
    AppBar,
    Box,
    Button,
    CssBaseline,
    IconButton,
    ThemeProvider,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { getTheme } from './config/theme';
import { I18nProvider, LANGUAGES, useI18n } from './i18n';
import { hideFooterBanner, showFooterBanner } from './monetization/admob';
import { AdsProvider, useAds } from './monetization/AdsProvider';
import { AppFooterAd } from './monetization/AppFooterAd';
import { config, isNative, webFooterAdConfigured } from './monetization/config';
import { RootState } from './store';
import { AboutPage } from './tomato/AboutPage';
import { SettingsMenu } from './tomato/components/SettingsMenu';
import { SupportDialog } from './tomato/components/SupportDialog';
import { playCue } from './tomato/feedback';
import { PomodoroPage } from './tomato/PomodoroPage';
import { PrivacyPage } from './tomato/PrivacyPage';

const Home: React.FC = () => {
    const { adsEnabled } = useAds();
    const { t, language, setLanguage } = useI18n();
    const dispatch = useDispatch();
    const themeMode = useSelector((s: RootState) => s.preferences.theme);
    const onThemeChange = (m: ThemeMode) => dispatch(setTheme(m));
    const [customizing, setCustomizing] = useState(false);
    const [supporting, setSupporting] = useState(false);
    const [settings, setSettings] = useState(false);
    const showFooter = !isNative() && adsEnabled && webFooterAdConfigured();
    const showNativeBanner = isNative() && adsEnabled;
    useEffect(() => {
        if (!showNativeBanner) return undefined;
        showFooterBanner();
        return () => {
            hideFooterBanner();
        };
    }, [showNativeBanner]);

    return (
        // A fixed wrapper, not a height inherited from #root: the AdSense script writes
        // `height: auto !important` onto the ancestors of its <ins>, which collapsed the page.
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                // The scene fills behind the header and, in the phone app, behind the system bars
                // (see MainActivity.java); the header and the controls clear them themselves.
                pr: 'var(--inset-right, env(safe-area-inset-right, 0px))',
                pb: isNative()
                    ? 0
                    : 'var(--inset-bottom, env(safe-area-inset-bottom, 0px))',
                pl: 'var(--inset-left, env(safe-area-inset-left, 0px))',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <AppBar
                position="absolute"
                color="transparent"
                elevation={0}
                sx={{
                    top: 0,
                    pt: 'var(--inset-top, env(safe-area-inset-top, 0px))',
                }}
            >
                <Toolbar
                    sx={{
                        gap: 0.5,
                        px: { xs: 1, sm: 2 },
                        // Round paper fill behind the header buttons, so they read on any scene.
                        '& .MuiIconButton-root, & .MuiButton-root': {
                            bgcolor: (t) =>
                                alpha(t.palette.background.paper, 0.9),
                            boxShadow: 1,
                            '&:hover': {
                                bgcolor: 'background.paper',
                            },
                        },
                        '& .MuiIconButton-root': { borderRadius: '50%' },
                        '& .MuiButton-root': { px: 1.75 },
                    }}
                >
                    <Typography
                        variant="h6"
                        component="h1"
                        noWrap
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: 800,
                            fontSize: { xs: 19, sm: 24 },
                        }}
                    >
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/favicon.png`}
                            alt=""
                            sx={{
                                width: { xs: 28, sm: 34 },
                                height: { xs: 28, sm: 34 },
                                mr: 1,
                                verticalAlign: 'middle',
                            }}
                        />
                        Tomato Pop
                    </Typography>
                    <GetOnMobileButton
                        appName={t('app.name')}
                        description={t('app.getOnMobile')}
                        labels={{
                            button: t('app.getOnMobileButton'),
                            title: (name) =>
                                t('app.getOnMobileTitle', { name }),
                            close: t('app.close'),
                            googlePlay: t('store.googlePlay'),
                            appStore: t('store.appStore'),
                            comingSoon: t('store.comingSoon'),
                            comingSoonGooglePlay: t(
                                'store.comingSoonGooglePlay'
                            ),
                            comingSoonAppStore: t('store.comingSoonAppStore'),
                        }}
                        packageName={config.androidPackage}
                        appStoreId={config.iosAppId}
                        androidStatus={
                            config.androidLive ? 'active' : 'comingSoon'
                        }
                        iosStatus={config.iosAppId ? 'active' : 'comingSoon'}
                    />
                    <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        aria-label={t('app.supportAria')}
                        startIcon={<FavoriteRoundedIcon />}
                        sx={{
                            minWidth: 0,
                            '& .MuiButton-startIcon': {
                                mr: { xs: 0, sm: 1 },
                                ml: { xs: 0, sm: -0.5 },
                            },
                        }}
                        onClick={() => {
                            playCue('ui');
                            setSupporting(true);
                        }}
                    >
                        <Box
                            component="span"
                            sx={{ display: { xs: 'none', sm: 'inline' } }}
                        >
                            {t('app.support')}
                        </Box>
                    </Button>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        <LanguageSelector
                            value={language}
                            languages={LANGUAGES}
                            onChange={setLanguage}
                            label={t('app.language')}
                        />
                        <ThemePicker
                            value={themeMode}
                            onChange={onThemeChange}
                            label={t('app.theme')}
                            labels={{
                                light: t('app.themeLight'),
                                dark: t('app.themeDark'),
                                system: t('app.themeSystem'),
                                daytime: t('app.themeDaytime'),
                            }}
                        />
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        <Tooltip title={t('app.settings')}>
                            <IconButton
                                color="inherit"
                                aria-label={t('app.settings')}
                                onClick={() => {
                                    playCue('ui');
                                    setSettings(true);
                                }}
                            >
                                <SettingsRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Tooltip title={t('app.customize')}>
                        <IconButton
                            color={customizing ? 'primary' : 'inherit'}
                            aria-label={t('app.customize')}
                            aria-pressed={customizing}
                            onClick={() => {
                                playCue('ui');
                                setCustomizing((v) => !v);
                            }}
                        >
                            <BrushRoundedIcon />
                        </IconButton>
                    </Tooltip>
                    <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                        <SettingsMenu
                            theme={themeMode}
                            onThemeChange={onThemeChange}
                            onOpenTimer={() => setSettings(true)}
                        />
                    </Box>
                </Toolbar>
            </AppBar>
            <PomodoroPage
                drawerOpen={customizing}
                onCloseDrawer={() => setCustomizing(false)}
                settingsOpen={settings}
                onCloseSettings={() => setSettings(false)}
                footerOffset={showFooter || showNativeBanner}
                nativeBanner={showNativeBanner}
            />
            {showFooter && <AppFooterAd />}
            <SupportDialog
                open={supporting}
                onClose={() => setSupporting(false)}
            />
        </Box>
    );
};

const PullToRefreshHost: React.FC = () => {
    const { t } = useI18n();
    return (
        <EdgePullToRefresh
            label={t('app.pullToRefresh')}
            readyIcon={<Emoji emoji="🍅" size={24} />}
        />
    );
};

function App() {
    const stored = useSelector((s: RootState) => s.preferences.theme);
    const mode = useResolvedThemeMode(stored);
    const theme = useMemo(() => getTheme(mode), [mode]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <I18nProvider>
                <PullToRefreshHost />
                <BrowserRouter>
                    <AdsProvider>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route
                                path="/privacy-policy"
                                element={<PrivacyPage />}
                            />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="*" element={<Home />} />
                        </Routes>
                        {!isNative() && <PwaInstallPrompt />}
                    </AdsProvider>
                </BrowserRouter>
            </I18nProvider>
        </ThemeProvider>
    );
}

export default App;
